import React, { useCallback, useState } from 'react';
import { ScanResult, PageOutcome, PageRole, SkipReason, ScreenshotRef, VisitedPage } from '../../utils/types';
import { T } from '../../designTokens';
import { useReportScreenshots } from '../../hooks/useReportScreenshots';
import ScreenshotLightbox from './ScreenshotLightbox';
import { pageCounts, plural } from './page-counts';

interface PagesAssessedProps {
  result: ScanResult;
  openSection: string | null;
  onToggle: (section: string) => void;
  /** Persistent report id; screenshots are addressed by it. Falls back to result.reportId. */
  reportId?: string | null;
  /** Share token for a report opened through a share link; forwarded to the image fetch. */
  shareToken?: string | null;
}

/** Customer-facing wording; the raw enum values never reach the page. */
const OUTCOME_LABEL: Record<PageOutcome, string> = {
  read: 'Read in full',
  thin: 'Almost no text on the page',
  unreachable: "Couldn't load this page",
  'http-error': 'The site returned an error for this page',
  'unreadable-pdf': "Couldn't read this PDF",
  blocked: 'Blocked before loading',
};

const OUTCOME_COLOR: Record<PageOutcome, string> = {
  read: T.success,
  thin: T.warning,
  unreachable: T.error,
  'http-error': T.error,
  'unreadable-pdf': T.warning,
  blocked: T.gray500,
};

export const ROLE_LABEL: Record<PageRole, string> = {
  homepage: 'Starting page',
  crawled: 'Found while crawling',
  policy: 'Privacy policy',
  'related-policy': 'Linked from the policy',
  dnss: 'Do Not Sell page',
  'policy-pdf': 'Policy PDF',
  'gpc-revisit': 'Re-checked with opt-out signal',
  'consent-sim': 'Re-checked after simulating consent',
  'form-page': 'Sign-in or register page',
};

const SKIP_LABEL: Record<SkipReason, string> = {
  'off-site': 'On a different domain',
  'excluded-pattern': 'Excluded from crawling',
  'crawl-budget': 'Beyond this scan’s page limit',
  'seed-budget': 'Beyond this scan’s policy-page limit',
  unscannable: 'Not a scannable address',
  'near-miss': "Looked privacy-related but didn't match a known page type",
};

const THUMB_WIDTH = 160;

/**
 * One thumbnail's inputs. Only the fields the thumb reads, so the sales
 * view's flattened SalesScreenshotRef (#1393) can drive it too.
 */
export interface Shot {
  page: Pick<VisitedPage, 'url'>;
  ref: Pick<ScreenshotRef, 'key' | 'role' | 'width' | 'height'>;
}

/**
 * One proof-of-scan thumbnail (DESIGN.md: radius-md, 1px gray-200 border,
 * caption in the label scale). Disabled while its blob is loading (src is
 * `undefined`), so the placeholder box keeps the strip's layout stable, and
 * stays disabled with a terminal "unavailable" caption when the fetch failed
 * (src is `null`) so a failure is never indistinguishable from "still
 * loading".
 */
export function ScreenshotThumb({
  shot,
  src,
  onOpen,
}: {
  shot: Shot;
  src?: string | null;
  onOpen: (shot: Shot) => void;
}) {
  const label = ROLE_LABEL[shot.ref.role];
  const ratioHeight = Math.round((THUMB_WIDTH * shot.ref.height) / shot.ref.width);
  const height = Number.isFinite(ratioHeight) && ratioHeight > 0 ? ratioHeight : Math.round(THUMB_WIDTH * 0.5625);
  const failed = src === null;
  return (
    <figure style={{ margin: 0, width: THUMB_WIDTH, flex: '0 0 auto' }}>
      <button
        type="button"
        onClick={() => onOpen(shot)}
        disabled={!src}
        aria-label={
          failed ? `Screenshot unavailable: ${label}, ${shot.page.url}` : `Open screenshot: ${label}, ${shot.page.url}`
        }
        style={{
          display: 'block',
          width: THUMB_WIDTH,
          height,
          padding: 0,
          border: `1px solid ${T.gray200}`,
          borderRadius: 6,
          overflow: 'hidden',
          backgroundColor: T.gray100,
          cursor: src ? 'zoom-in' : 'default',
        }}
      >
        {src ? (
          <img
            src={src}
            alt={`${label}: ${shot.page.url}`}
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
          />
        ) : null}
        {failed ? (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              fontSize: 12,
              fontWeight: 500,
              color: T.gray500,
              textAlign: 'center',
              padding: '0 8px',
            }}
          >
            Screenshot unavailable
          </span>
        ) : null}
      </button>
      <figcaption
        style={{
          marginTop: 4,
          fontSize: 12,
          fontWeight: 500,
          color: T.gray500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </figcaption>
    </figure>
  );
}

export default function PagesAssessed({ result, openSection, onToggle, reportId, shareToken }: PagesAssessedProps) {
  const ledger = result.pageLedger;
  const effectiveReportId = reportId ?? result.reportId ?? null;

  // Hooks run before the early return below, so their order is stable.
  const shots: Shot[] = (ledger?.visited ?? []).flatMap((page) =>
    (page.screenshots ?? []).map((ref) => ({ page, ref })),
  );
  const hasShots = Boolean(effectiveReportId) && shots.length > 0;
  const sources = useReportScreenshots(hasShots ? effectiveReportId : null, shots.map((s) => s.ref.key), shareToken);
  const [openShot, setOpenShot] = useState<Shot | null>(null);
  const closeLightbox = useCallback(() => setOpenShot(null), []);
  const openLightboxSrc = openShot ? sources[openShot.ref.key] : undefined;

  if (!ledger || ledger.visited.length === 0) return null;

  const read = ledger.visited.filter((p) => p.outcome === 'read').length;
  const problems = ledger.visited.length - read;
  const isOpen = openSection === 'pages';

  // Leads with the hero's own page count (#1661) and labels the policy pages
  // on top of it, so this meta and the hero stat never read as two answers.
  const { scanned, policyRead } = pageCounts(result);
  const summary = [
    scanned !== null ? `${plural(scanned, 'page')} scanned` : null,
    policyRead > 0 ? `${plural(policyRead, 'policy page')} read` : null,
    problems > 0 ? `${problems} with problems` : null,
    ledger.skipped.length > 0 ? `${ledger.skipped.length} considered and skipped` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const renderThumb = (shot: Shot) => (
    <ScreenshotThumb key={shot.ref.key} shot={shot} src={sources[shot.ref.key]} onOpen={setOpenShot} />
  );

  return (
    <div id="appendix-pages" style={{ marginBottom: 32 }}>
      <div className="report-section-header">
        <span className="report-section-header__num">07</span>
        <h2 className="report-section-header__title">Pages assessed</h2>
        <span className="report-section-header__meta">{summary}</span>
      </div>

      {hasShots && (
        <div
          className="pages-assessed__strip"
          style={{ display: 'flex', gap: 12, overflowX: 'auto', padding: '4px 0 12px' }}
        >
          {shots.map(renderThumb)}
        </div>
      )}

      <button onClick={() => onToggle('pages')} className="tracker-summary__table-btn">
        {isOpen ? 'Hide page list' : 'View every page we looked at'}
      </button>

      {isOpen && (
        <div className="tracker-summary__table-wrapper">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 8, color: T.gray500 }}>Page</th>
                <th style={{ textAlign: 'left', padding: 8, color: T.gray500 }}>Why we loaded it</th>
                <th style={{ textAlign: 'left', padding: 8, color: T.gray500 }}>Result</th>
                {hasShots && <th style={{ textAlign: 'left', padding: 8, color: T.gray500 }}>What we saw</th>}
              </tr>
            </thead>
            <tbody>
              {ledger.visited.map((page) => (
                <tr key={page.url}>
                  <td style={{ padding: 8, wordBreak: 'break-all' }}>
                    <a href={page.url} target="_blank" rel="noopener noreferrer">{page.url}</a>
                  </td>
                  <td style={{ padding: 8 }}>{page.roles.map((r) => ROLE_LABEL[r]).join(', ')}</td>
                  <td style={{ padding: 8, color: OUTCOME_COLOR[page.outcome] }}>
                    {OUTCOME_LABEL[page.outcome]}
                    {page.httpStatus && page.httpStatus >= 400 ? ` (HTTP ${page.httpStatus})` : ''}
                  </td>
                  {hasShots && (
                    <td style={{ padding: 8, verticalAlign: 'top' }}>
                      {page.screenshots && page.screenshots.length > 0 ? (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {page.screenshots.map((ref) => renderThumb({ page, ref }))}
                        </div>
                      ) : (
                        <span style={{ color: T.gray400 }}>No screenshot</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {ledger.skipped.length > 0 && (
            <>
              <h3 style={{ marginTop: 24, color: T.gray700 }}>Considered and skipped</h3>
              <ul>
                {ledger.skipped.map((s) => (
                  <li key={s.url} style={{ marginBottom: 4, wordBreak: 'break-all' }}>
                    <span>{s.url}</span>
                    <span style={{ color: T.gray500 }}> · {SKIP_LABEL[s.reason]}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          {ledger.truncated && (
            <p style={{ color: T.gray500, marginTop: 12 }}>
              This list was shortened; the scan looked at more pages than are shown here.
            </p>
          )}
        </div>
      )}

      {openShot && openLightboxSrc && (
        <ScreenshotLightbox
          src={openLightboxSrc}
          url={openShot.page.url}
          roleLabel={ROLE_LABEL[openShot.ref.role]}
          width={openShot.ref.width}
          height={openShot.ref.height}
          onClose={closeLightbox}
        />
      )}
    </div>
  );
}
